import router from '@/router'
import { MessageService } from '@/services/message.service'
import { NewsFeedService } from '@/services/newsfeed.service'
import { SurveyService } from '@/services/survey.service'
import { UserService } from '@/services/user.service'
import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import { commitPaginationFromMeta, showErrorToast } from './helpers'

export const contentActions = {
  async loadMessages({ commit }, data) {
    try {
      const response = await MessageService.getMessages(data)
      commit('SET_MESSAGES', response.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadMessagesList({ commit }, params = {}) {
    try {
      const response = await MessageService.listMessages(params)
      commit('SET_MESSAGES', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadMessage({ commit }, id) {
    try {
      const response = await MessageService.getMessage(id)
      commit('SET_MESSAGE', response.data.data)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async storeMessage({ commit }, data) {
    try {
      const response = await MessageService.storeMessage(data)
      if (response) {
        router.push({ name: 'messages_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async saveMessage({ commit }, data) {
    try {
      const response = await MessageService.saveMessage(data, data.id)
      if (response) {
        router.push({ name: 'messages_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteMessage({ commit }, id) {
    try {
      return await MessageService.deleteMessage(id)
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async createUpdateThreads({ commit }, data) {
    try {
      const response = await UserService.createUpdateThreads(data)
      if (response) {
        router.push({ name: 'threads' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadSurveys({ commit }) {
    const response = await SurveyService.surveys()
    commit('SET_SURVEYS', response.data.data)
  },

  async loadSurveysActivated({ commit }) {
    const response = await SurveyService.surveysActivated()
    commit('SET_SURVEYS_ACTIVATED', response.data.data)
  },

  async storeSurvey({ commit }, data) {
    try {
      const response = await SurveyService.storeSurvey(data)
      if (response) {
        router.push({ name: 'surveys_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async updateSurvey(_, data) {
    try {
      const response = await SurveyService.updateSurvey(data, data.id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async removeSurvey({ commit }, id) {
    try {
      const response = await SurveyService.removeSurvey(id)
      if (response) {
        router.push({ name: 'surveys_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async setActivatedSurvey(_, id) {
    try {
      const response = await SurveyService.setActivatedSurvey(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadSurveyById({ commit }, id) {
    const response = await SurveyService.survey(id)
    commit('SET_SURVEY', response.data.data)
    commit('SET_SURVEY_GROUPS', response.data.data.groups)
  },

  updateValueSurveyGroups({ commit }, data) {
    commit('SET_SURVEY_GROUPS', data)
  },

  async newNewsFeed(_, data) {
    try {
      const response = await NewsFeedService.newNewsFeed({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        router.push({
          name: 'news_feed_edit',
          params: { id: response.data.news_feed_id }
        })
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadNewsFeeds({ commit }, params = {}) {
    try {
      const normalized =
        typeof params === 'number' || typeof params === 'string'
          ? { page: params }
          : params

      const response = await NewsFeedService.newsFeeds({
        page: normalized.page ?? 1,
        per_page: normalized.per_page ?? 15,
        search: normalized.search ?? null,
      })
      commit('SET_NEWS_FEEDS', response.data.data)
      commitPaginationFromMeta(commit, response)
      return { meta: response.data.meta }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadNewsFeedById({ commit }, id) {
    const response = await NewsFeedService.newsFeed(id)
    commit('SET_NEWS_FEED', response.data.data)
  },

  async updateNewsFeed(_, data) {
    try {
      const response = await NewsFeedService.updateNewsFeed(data.id, data)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteNewsFeed(_, id) {
    try {
      const response = await NewsFeedService.deleteNewsFeedById(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async deleteNewsFeedSlide(_, id) {
    try {
      const response = await NewsFeedService.deleteNewsFeedSlide(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async orderNews({ commit }, payload) {
    const updates = Array.isArray(payload) ? payload : payload.updates
    const skipCommit = Array.isArray(payload) ? false : Boolean(payload.skipCommit)

    try {
      const response = await NewsFeedService.reorderNews(updates)

      if (response.status === HTTP_STATUS_CODES.OK) {
        if (!skipCommit) {
          commit('REORDER_NEWS_FEEDS', updates)
        }
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async updateNewsFeedOrder({ commit }, payload) {
    const { id, order, skipCommit = false } = payload

    try {
      const response = await NewsFeedService.updateNewsFeedOrder(id, order)

      if (response.status === HTTP_STATUS_CODES.OK) {
        if (!skipCommit) {
          commit('UPDATE_NEWS_FEED_ORDER', { id, order })
        }
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },
}
